
import { useEffect, useState } from 'react';

interface EnergyFlowDiagramProps {
  powerFlowData: any;
}

export function EnergyFlowDiagram({ powerFlowData }: EnergyFlowDiagramProps) {
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationKey(prev => prev + 1);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  if (!powerFlowData) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 h-96 flex items-center justify-center">
        <div className="text-center text-gray-500">
          <i className="ri-loader-4-line animate-spin text-4xl mb-2"></i>
          <p>Cargando flujo energético...</p>
        </div>
      </div>
    );
  }

  const { P_PV, P_Load, P_Grid, Battery } = powerFlowData;
  
  // Determinar direcciones de flujo
  const pvToHome = P_PV > 0;
  const gridToHome = P_Grid > 0;
  const homeToGrid = P_Grid < 0;
  const batteryCharging = Battery?.P_Battery && Battery.P_Battery < 0;
  const batteryDischarging = Battery?.P_Battery && Battery.P_Battery > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-cyan-200 h-96">
      <div className="relative h-full flex items-center justify-center">
        {/* Diagrama de flujo energético */}
        <div className="relative w-full h-full max-w-md">
          
          {/* Panel Solar (arriba) */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2">
            <div className={`w-24 h-18 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex flex-col items-center justify-center text-white shadow-lg ${pvToHome ? 'animate-pulse' : ''}`}>
              <i className="ri-sun-line text-2xl mb-1"></i>
              <span className="text-xs font-bold">{P_PV}W</span>
            </div>
            <div className="text-center mt-2">
              <span className="text-xs font-semibold text-gray-700">Panel Solar</span>
            </div>
          </div>

          {/* Casa/Hogar (centro) */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="w-28 h-22 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex flex-col items-center justify-center text-white shadow-lg">
              <i className="ri-home-4-line text-2xl mb-1"></i>
              <span className="text-xs font-bold">{P_Load}W</span>
            </div>
            <div className="text-center mt-2">
              <span className="text-xs font-semibold text-gray-700">Hogar</span>
            </div>
          </div>

          {/* Red Eléctrica (derecha) */}
          <div className="absolute top-1/2 right-0 transform -translate-y-1/2">
            <div className={`w-24 h-18 bg-gradient-to-br ${homeToGrid ? 'from-green-500 to-emerald-600' : 'from-cyan-600 to-blue-600'} rounded-xl flex flex-col items-center justify-center text-white shadow-lg ${(gridToHome || homeToGrid) ? 'animate-pulse' : ''}`}>
              <i className={`${homeToGrid ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-2xl mb-1`}></i>
              <span className="text-xs font-bold">{Math.abs(P_Grid)}W</span>
            </div>
            <div className="text-center mt-2">
              <span className="text-xs font-semibold text-gray-700">Red</span>
            </div>
          </div>

          {/* Batería (abajo) */}
          {Battery && (
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
              <div className={`w-24 h-18 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex flex-col items-center justify-center text-white shadow-lg ${(batteryCharging || batteryDischarging) ? 'animate-pulse' : ''}`}>
                <i className="ri-battery-charge-line text-2xl mb-1"></i>
                <span className="text-xs font-bold">{Battery.SOC}%</span>
              </div>
              <div className="text-center mt-2">
                <span className="text-xs font-semibold text-gray-700">Batería</span>
              </div>
            </div>
          )}

          {/* Flechas animadas */}
          {/* Solar a Casa */}
          {pvToHome && (
            <div className="absolute top-20 left-1/2 transform -translate-x-1/2">
              <div key={`pv-home-${animationKey}`} className="w-1 h-16 bg-gradient-to-b from-yellow-400 to-cyan-600 animate-pulse"></div>
              <i className="ri-arrow-down-line text-cyan-600 absolute top-10 left-1/2 transform -translate-x-1/2 animate-bounce"></i>
            </div>
          )}

          {/* Casa a Red / Red a Casa */}
          <div className="absolute top-1/2 left-3/4 transform -translate-y-1/2">
            <div key={`grid-${animationKey}`} className={`w-16 h-1 bg-gradient-to-r ${homeToGrid ? 'from-green-500 to-emerald-600' : 'from-cyan-600 to-blue-600'} animate-pulse`}></div>
            <i className={`${homeToGrid ? 'ri-arrow-right-line text-emerald-600' : 'ri-arrow-left-line text-cyan-600'} absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-bounce`}></i>
          </div>

          {/* Casa a Batería / Batería a Casa */}
          {Battery && (
            <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2">
              <div key={`battery-${animationKey}`} className={`w-1 h-16 bg-gradient-to-b ${batteryCharging ? 'from-cyan-600 to-emerald-600' : 'from-emerald-600 to-cyan-600'} animate-pulse`}></div>
              <i className={`${batteryCharging ? 'ri-arrow-down-line text-emerald-600' : 'ri-arrow-up-line text-cyan-600'} absolute top-8 left-1/2 transform -translate-x-1/2 animate-bounce`}></i>
            </div>
          )}
        </div>

        {/* Leyenda */}
        <div className="absolute bottom-4 right-4 bg-white border border-cyan-200 rounded-lg p-3 text-xs shadow-sm">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <span>Generación Solar</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-cyan-600 rounded-full"></div>
              <span>Consumo Hogar</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 ${homeToGrid ? 'bg-emerald-600' : 'bg-cyan-600'} rounded-full`}></div>
              <span>{homeToGrid ? 'Venta' : 'Compra'} Red</span>
            </div>
            {Battery && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-emerald-600 rounded-full"></div>
                <span>Batería {Battery.Status}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
